import React from "react";
import {IconButton, Link, Typography} from "@mui/material";
import {Blue3, Grey5} from "../../theme/styles";
import useBreakpoint from "../../util/useBreakpoint";
import css_buttons from "./css/CCButton.module.scss";

// The dashboard tile grid, shared by every page that presents a set of destinations as cards.
//
// It lives here rather than in a page because the layout is the part that drifts: the Administration
// dashboard it came from had five hand-written copies of this grid, one per breakpoint, and tiles
// added to one copy never appeared in the others. A second page copying the markup would start that
// drift again, so the layout is defined once and the pages supply only the tiles.
//
// A tile carries its own action rather than a route: the Documentation tiles open a guide in a popup
// window, so "where this tile goes" is not always something the grid can navigate to.

export type Tile = {
    key: string;
    icon: React.ReactNode;
    label: string;
    description: string;
    actionLabel: string;
    onAction: () => void;
};

// The tile's box. A filler cell is built from the SAME style so a short last row lines up with the
// rows above it.
//
// EQUAL SHARES OF THE ROW, NOT A PERCENTAGE PLUS PADDING. This was `width: 25%` on a CONTENT-BOX
// with 15px of padding and a 1px border, plus a 10px right margin -- so four tiles asked for 100%
// of the row and then another 168px on top. The row had nothing left to distribute, space-evenly
// gave no edge gap, and the grid sat hard against the sidebar while the last tile ran off the
// right. Ed: "the left side panes are flush with the left side menu bar."
//
// `flex: 1 1 0` divides whatever the row actually has; border-box keeps the padding inside that
// share; the gap on the row replaces the margins, so the arithmetic cannot drift again.
const tileBox = (): React.CSSProperties => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    marginBottom: "10px",
    border: "1px solid #EDEFF3",
    borderRadius: "10px",
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "15px",
});

// Tiles per row, and the width each one takes at that count.
const COLUMNS: Record<string, number> = {xl: 4, lg: 3, md: 2, sm: 1, xs: 1};

export default function CCTileGrid({tiles}: {tiles: Tile[]}) {
    const breakpoint = useBreakpoint();

    function renderTile(tile: Tile) {
        return (
            <div key={tile.key} style={tileBox()}>
                <div style={{display: "flex", flexDirection: "row"}}>
                    <IconButton className={css_buttons.blueIconButton} style={{height: "75px"}} onClick={tile.onAction}>
                        {tile.icon}
                    </IconButton>
                    <div style={{marginLeft: "15px", marginTop: "25px"}}>
                        <Link underline="none" variant="inherit" color="inherit" href="#" onClick={e => { e.preventDefault(); tile.onAction(); }}>
                            <Typography style={{fontSize: "16px", fontWeight: "400", color: "#000000", textAlign: "left"}}>{tile.label}</Typography>
                        </Link>
                    </div>
                </div>
                <div style={{marginTop: "10px", height: "42px"}}>
                    <Typography style={{fontSize: "14px", fontWeight: "400", color: Grey5, textAlign: "left"}}>{tile.description}</Typography>
                </div>
                <div style={{marginTop: "20px", borderBottom: "1px solid #EDEFF3"}}/>
                <div style={{marginTop: "10px"}}>
                    <Link underline="none" variant="inherit" color="inherit" href="#" onClick={e => { e.preventDefault(); tile.onAction(); }}>
                        <Typography style={{fontSize: "13px", fontWeight: 600, color: Blue3, marginTop: "10px"}}>{tile.actionLabel}</Typography>
                    </Link>
                </div>
            </div>
        );
    }

    const columns = COLUMNS[breakpoint.getBreakPointName()] ?? 4;

    const rows: Tile[][] = [];
    for (let i = 0; i < tiles.length; i += columns) rows.push(tiles.slice(i, i + columns));

    return (
        // THE SAME GUTTER THE HEADER USES. The blue bar indents its title 20px; the grid had no
        // horizontal padding at all, so the tiles began nearer the rail than the page's own title.
        <div style={{padding: "0 20px"}}>
            {rows.map((row, index) => (
                <div key={index} style={{display: "flex", flexDirection: "row", gap: "10px", height: "240px", marginTop: index === 0 ? "20px" : undefined}}>
                    {row.map(tile => renderTile(tile))}
                    {/* Fillers so a short last row lines up with the rows above: same box, no
                        content, no visible border. */}
                    {Array.from({length: columns - row.length}, (_, k) => (
                        <div key={"pad" + k} style={{...tileBox(), borderColor: "transparent", visibility: "hidden"}}/>
                    ))}
                </div>
            ))}
        </div>
    );
}
